import { useEffect, useMemo } from 'react';
import { AppLayoutSidebar } from './AppLayout.sidebar';
import { HideSidebarProvider, useHideSidebarRequests } from './AppLayout.sidebar-visibility';
import clsx from 'clsx';
import { useUserContext } from '@/app/context/UserContext';
import { useCompanyContext } from '@/app/context/CompanyContext';
import { useMarleeContext } from '@/app/context/MarleeContext';
import { usePathname } from 'next/navigation';
import { pathToDisableTopNav } from '@/app/utils/constants';
import { checkDisableNav } from '@/app/utils/check-disable-nav.util';
import { CompanyAccessDisabledScreen } from './AppLayout.companyAccessDisabled';
import { isPublicPageRoute } from '@/utils/isPublicPageRoute.util';
import { Box, Button, Typography } from '@mui/material';
import dynamic from 'next/dynamic';
export { useHideAppSidebar } from './AppLayout.sidebar-visibility';
const MarleeChatPanel = dynamic(() => import('@/app/components/marlee/MarleeChatPanel'), {
  ssr: false
});
const NoRolesAssignedScreen = ({
  onLogout,
  onSwitchCompany
}) => {
  return <Box className="flex min-h-screen w-full flex-col items-center justify-center gap-6 bg-page text-foreground">
            <div className="w-80 mx-auto flex flex-col items-center justify-center gap-4">
                <Typography variant="h6" component="h1" align="center" fontWeight={600}>
                    No Roles Assigned
                </Typography>
                <Typography variant="body1" color="text.secondary" align="center" sx={{
        maxWidth: 480
      }}>
                    Your account is not assigned any roles for this organization. Please contact your administrator.
                </Typography>
                <Button variant="contained" disableElevation onClick={onSwitchCompany} fullWidth>
                    Switch Company
                </Button>
                <Button variant="outlined" color="error" disableElevation onClick={onLogout} fullWidth>
                    Logout
                </Button>
            </div>
        </Box>;
};
export const AppLayout = ({
  showHeader,
  children
}) => {
  const pathname = usePathname();
  const {
    isCompanyAccessDisabled,
    companyConfig
  } = useCompanyContext();
  const {
    user,
    clearUser,
    hasNoRolesInSelectedCompany
  } = useUserContext();
  const {
    isPageSettingEnabled
  } = user || {};
  const {
    togglePanel
  } = useMarleeContext();
  const {
    isHiddenByPage,
    requestHide
  } = useHideSidebarRequests();
  useEffect(() => {
    const handleKeyDown = e => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'i') {
        e.preventDefault();
        const isHiddenRoute = ['/create-jd', '/job-descriptions', '/settings', '/learning', '/engagement', '/approve'].some(r => pathname?.includes(r));
        if (companyConfig?.isMarleeEnabled === true && !isHiddenRoute) {
          togglePanel();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [togglePanel, companyConfig?.isMarleeEnabled, pathname]);
  const isSidebarHidden = useMemo(() => {
    if (!isPageSettingEnabled && checkDisableNav(pathname, pathToDisableTopNav)) {
      return true;
    }
    if (pathname === '/') return true;

    // Only hide sidebar for public interview pages at /{companySlug}/interview/{page}
    // NOT nested paths like /{companySlug}/recruitment/candidate/{id}/interview/...
    const isPublicInterviewPage = /^\/([^/]+)\/interview\//.test(pathname);
    if (isPublicInterviewPage || checkDisableNav(pathname, [`/career/screeningInterview`, `/recruitment/approve/`, `/recruitment/jobApplications/approve/`, `/recruitment/offers/approve/`, `/recruitment/offers/accept/`, '/recruitment/offers/upload-documents/', `/select-company`, `/docs`, '/candidate-review/', '/signup', '/onboarding', '/auth/signup'])) {
      return true;
    }
    return !showHeader;
  }, [showHeader, isPageSettingEnabled, pathname]);
  // A mounted page can also ask for it (useHideAppSidebar), when that depends on its state rather than the path.
  const hideSidebar = isSidebarHidden || isHiddenByPage;
  if (!isPublicPageRoute(pathname) && isCompanyAccessDisabled) {
    return <CompanyAccessDisabledScreen />;
  }
  if (!isPublicPageRoute(pathname) && hasNoRolesInSelectedCompany) {
    return <NoRolesAssignedScreen onSwitchCompany={() => {
      window.location.assign('/api/auth/switch-company');
    }} onLogout={() => {
      clearUser();
      setTimeout(() => {
        window.location.replace('/auth/logout');
      }, 100);
    }} />;
  }
  const isOnboardingRoute = pathname === '/auth/signup' || pathname?.startsWith('/onboarding') === true || pathname?.startsWith('/signup') === true;
  return <div className="flex flex-row" style={{
    background: isOnboardingRoute ? '#f6f6f6' : 'var(--canvas-gradient)'
  }}>
            <AppLayoutSidebar isHidden={hideSidebar} />
            {/* A page opts out of overscroll bounce by rendering data-page-overscroll="none" (e.g. CareerJobsView). */}
            <main className={clsx('h-dvh w-full overflow-y-auto no-scrollbar overscroll-none', !hideSidebar && 'pt-4')}>
                <div className={clsx('flex flex-col', !hideSidebar && 'rounded-tl-[10px] bg-page min-h-full')}>
                    {/* A page fills the remaining height by rendering data-fill-viewport (e.g. a full-height loading state). */}
                    <section className={clsx('flex-1 has-[[data-fill-viewport]]:flex has-[[data-fill-viewport]]:flex-col', hideSidebar ? 'py-0' : 'py-4')}>
                        <HideSidebarProvider value={requestHide}>{children}</HideSidebarProvider>
                    </section>
                </div>
            </main>
            {!pathname?.includes('/dashboard') && !pathname?.includes('/custom-charts') && <MarleeChatPanel />}
        </div>;
};